import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, rem } from '@bonusly/design-system';
import { useIsBonuslyCompany } from '@bonusly/who-am-i';

import { Panel } from '../../../../components/panel';
import { LeaderboardDetailView, LeaderboardExportData } from './leaderboard-detail-view';
import { LeaderboardExportBar } from './leaderboard-export-bar';
import { LeaderboardInitialView } from './leaderboard-initial-view';
import { LeaderboardDirection, LeaderboardPanelProps, LeaderboardState, ScopeType, TimeframeOption } from './types';

const getInitialState = (): LeaderboardState => ({
	view: 'initial',
	selectedHashtag: null,
	direction: 'received',
	scopeType: 'company',
	selectedTeam: null,
	selectedDepartment: null,
	selectedTimeframe: '30_days',
});

export const LeaderboardPanel: FC<LeaderboardPanelProps> = ({ isOpen, isSidebarExpanded, onClose }) => {
	const { t } = useTranslation('navigation');
	const isBonusly = useIsBonuslyCompany();
	const [state, setState] = useState<LeaderboardState>(getInitialState);
	// Track if detail view has been visited to keep it mounted for smooth back animation
	const [hasVisitedDetail, setHasVisitedDetail] = useState(false);
	const [exportData, setExportData] = useState<LeaderboardExportData | null>(null);
	const scrollRef = useRef<HTMLDivElement>(null);

	const handleExportDataChange = useCallback((data: LeaderboardExportData) => {
		setExportData(data);
	}, []);

	// Reset scroll position when view changes
	useEffect(() => {
		if (scrollRef.current) {
			scrollRef.current.scrollTop = 0;
		}
	}, [state.view]);

	// Reset state when panel is closed externally (via Zustand store or other means)
	useEffect(() => {
		if (!isOpen) {
			setState(getInitialState());
			setHasVisitedDetail(false);
			setExportData(null);
		}
	}, [isOpen]);

	const handleClose = () => {
		setState(getInitialState());
		setHasVisitedDetail(false);
		onClose();
	};

	const handleViewAll = (direction: LeaderboardDirection = 'received') => {
		setHasVisitedDetail(true);
		setState((prev) => ({
			...prev,
			view: 'detail',
			selectedHashtag: null,
			direction,
		}));
	};

	const handleViewHashtag = (hashtag: string) => {
		setHasVisitedDetail(true);
		setState((prev) => ({
			...prev,
			view: 'detail',
			selectedHashtag: hashtag,
			direction: 'received',
		}));
	};

	const handleBack = () => {
		setState((prev) => ({
			...prev,
			view: 'initial',
			selectedHashtag: null,
			direction: 'received',
		}));
		setExportData(null);
	};

	const handleScopeChange = (scopeType: ScopeType, teamId: string | null, departmentId: string | null) => {
		setState((prev) => ({
			...prev,
			scopeType,
			selectedTeam: teamId,
			selectedDepartment: departmentId,
		}));
	};

	const handleTimeframeChange = (timeframe: TimeframeOption) => {
		setState((prev) => ({
			...prev,
			selectedTimeframe: timeframe,
		}));
	};

	const handleCustomDateChange = (startDate: string, endDate: string) => {
		setState((prev) => ({
			...prev,
			selectedTimeframe: 'custom',
			customStartDate: startDate,
			customEndDate: endDate,
		}));
	};

	// Build title based on current view
	const getTitle = () => {
		if (state.view === 'initial') {
			return t('leaderboard.title');
		}
		// Detail view: show hashtag name, "Top givers", or "Most recognized"
		if (state.selectedHashtag) {
			return `#${state.selectedHashtag}`;
		}
		if (state.direction === 'given') {
			return t('leaderboard.topGivers');
		}
		return t('leaderboard.allCompany');
	};

	const showExportBar = isBonusly && state.view === 'detail' && exportData?.isReady;

	const footer = useMemo(() => {
		if (!showExportBar || !exportData) return undefined;
		return <LeaderboardExportBar totalCount={exportData.totalCount} filters={exportData.filters} isFetching={exportData.isFetching} />;
	}, [showExportBar, exportData]);

	return (
		<Panel
			isOpen={isOpen}
			onClose={handleClose}
			isSidebarExpanded={isSidebarExpanded}
			title={getTitle()}
			titleTooltip={state.view === 'initial' ? t('leaderboard.titleTooltip') : undefined}
			onBack={state.view === 'detail' ? handleBack : undefined}
			closeButtonLabel={t('leaderboard.closePanel')}
			closeOnClickOutside
			scrollRef={scrollRef}
			footer={footer}
			width={rem(363)}
			data-testid="leaderboard-panel"
		>
			<Box position="relative" overflow="hidden">
				{/* Initial View */}
				<Box
					position={state.view === 'initial' ? 'relative' : 'absolute'}
					top={0}
					left={0}
					right={0}
					transform={state.view === 'initial' ? 'translateX(0)' : 'translateX(-100%)'}
					opacity={state.view === 'initial' ? 1 : 0}
					transition="transform 0.25s ease-in-out, opacity 0.25s ease-in-out"
					pointerEvents={state.view === 'initial' ? 'auto' : 'none'}
				>
					<LeaderboardInitialView
						state={state}
						onViewAll={handleViewAll}
						onViewHashtag={handleViewHashtag}
						onScopeChange={handleScopeChange}
						onTimeframeChange={handleTimeframeChange}
						onCustomDateChange={handleCustomDateChange}
					/>
				</Box>

				{/* Detail View - mounted after first visit for smooth animations */}
				{hasVisitedDetail && (
					<Box
						position={state.view === 'detail' ? 'relative' : 'absolute'}
						top={0}
						left={0}
						right={0}
						transform={state.view === 'detail' ? 'translateX(0)' : 'translateX(100%)'}
						opacity={state.view === 'detail' ? 1 : 0}
						transition="transform 0.25s ease-in-out, opacity 0.25s ease-in-out"
						pointerEvents={state.view === 'detail' ? 'auto' : 'none'}
					>
						<LeaderboardDetailView
							state={state}
							onScopeChange={handleScopeChange}
							onTimeframeChange={handleTimeframeChange}
							onCustomDateChange={handleCustomDateChange}
							onExportDataChange={handleExportDataChange}
						/>
					</Box>
				)}
			</Box>
		</Panel>
	);
};
